/**
 * UI Component Style Constants
 * 
 * This file contains reusable style constants for UI components.
 * For color theme definitions, see: app/features/theme/constants/colorPalettes.ts
 */

// Button style classes for consistent usage
export const buttonStyles = {
  // Primary button (main CTA) - compact sizing - uses solid color with lighter hover for better readability
  primary: `bg-gradient-to-r from-primary to-accent font-semibold shadow-lg hover:shadow-xl hover:opacity-95 transition-all duration-300 min-w-[120px] px-5 py-1.5 text-sm rounded-full text-white`,
  
  // Secondary button (outline style) - compact sizing - no background on hover for better readability
  secondary: 'border-2 border-primary text-primary hover:border-primary/60 hover:text-primary font-semibold transition-all duration-300 min-w-[120px] px-5 py-1.5 text-sm rounded-full',
  
  // Outline button - compact sizing - no background on hover for better readability
  outline: 'border border-primary text-primary hover:border-primary/60 hover:text-primary transition-all duration-300 min-w-[120px] px-5 py-1.5 text-sm rounded-full',
  
  // Ghost button (minimal) - compact sizing - uses theme colors
  ghost: 'text-primary hover:bg-accent transition-all duration-300 min-w-[120px] px-5 py-1.5 text-sm rounded-full',
  
  // Destructive button (for delete actions) - compact sizing
  destructive: 'bg-red-600 hover:bg-red-700 text-white font-semibold transition-all duration-300 min-w-[120px] px-5 py-1.5 text-sm rounded-full',
  
  // Full width button (for forms) - maintains full width but compact other styling - uses gradient
  fullWidth: `btn-gradient font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 w-full px-5 py-1.5 text-sm rounded-full text-white`,
} as const;

// Icon size constants for consistent usage
export const iconSizes = {
  // Standard icon size for buttons
  button: 'w-4 h-4',
  
  // Small icon size
  small: 'w-3 h-3',
  
  // Large icon size for hero sections
  large: 'w-10 h-10',
  
  // Extra large for feature cards
  extraLarge: 'w-12 h-12',
} as const;

// Text color classes for theme consistency
export const textColors = {
  // Brand text colors - uses dynamic gradient with theme colors
  brand: 'font-bold',
  
  // Link colors - uses theme colors
  link: 'text-primary hover:opacity-80',
  
  // Accent text - uses theme colors
  accent: 'text-primary',
  secondary: 'text-accent',
} as const;

// Icon colors for consistency (uses Tailwind color classes)
export const iconColors = {
  primary: 'text-primary',
  secondary: 'text-accent',
  accent: 'text-ring',
} as const;

// Tab styles for consistent tab appearance across all pages
export const tabStyles = {
  // Active tab trigger — light primary tint with bold text
  activeTrigger: 'data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:font-semibold',
  // Responsive text size for tab triggers
  responsiveText: 'text-xs sm:text-sm',
} as const;

// Alert dialog content styles for consistent dialog appearance
export const dialogStyles = {
  content: 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700',
} as const;

// Badge style constants for consistent status and tag colors across all pages
export const badgeStyles = {
  // Status badges — semantic colors
  status: {
    active: 'bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800',
    inactive: '', // uses default secondary variant
    warning: 'bg-amber-100 text-amber-700 border-amber-200 shadow-none dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
    error: 'bg-red-100 text-red-700 border-red-200 shadow-none dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
    default: 'bg-sky-100 text-sky-700 border-sky-200 shadow-none dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800',
  },
  // Classification badges
  classification: {
    builtIn: 'bg-amber-100 text-amber-800 border-amber-200 shadow-none dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700',
  },
  // Tenant classification badges
  tenantClassification: {
    normal: 'bg-sky-100 text-sky-700 border-sky-200 shadow-none dark:bg-sky-900/30 dark:text-sky-400 dark:border-sky-800',
    business: 'bg-blue-100 text-blue-700 border-blue-200 shadow-none dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
    enterprise: 'bg-violet-100 text-violet-700 border-violet-200 shadow-none dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800',
    trial: 'bg-amber-100 text-amber-700 border-amber-200 shadow-none dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
  },
  // Tenant type badges
  tenantType: {
    standard: 'bg-teal-100 text-teal-700 border-teal-200 shadow-none dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800',
    premium: 'bg-indigo-100 text-indigo-700 border-indigo-200 shadow-none dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800',
    enterprise: 'bg-purple-100 text-purple-700 border-purple-200 shadow-none dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
  },
  // Role type badges
  roleType: {
    system: 'bg-purple-100 text-purple-700 border-purple-200 shadow-none dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
    tenant: 'bg-indigo-100 text-indigo-700 border-indigo-200 shadow-none dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800',
    admin: 'bg-violet-100 text-violet-700 border-violet-200 shadow-none dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800',
    api: 'bg-cyan-100 text-cyan-700 border-cyan-200 shadow-none dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-800',
  },
} as const;

// Tag badge color palette — 20 visually distinct colors, sequential assignment.
// RESERVED (not used here): emerald/green (active), grey/slate (inactive), amber/yellow (warning), red/rose (error), sky (default).
// Two tiers: 1-10 are light (100), 11-20 are deeper (200) — same hues, different intensity.
// Order: cool → neutral → warm. Red-ish colors (pink, orange, fuchsia) placed last.
const TAG_COLORS = [
  'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',               //  1. blue
  'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800',               //  2. teal
  'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-800',               //  3. cyan
  'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800',     //  4. indigo
  'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400 dark:border-violet-800',     //  5. violet
  'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',     //  6. purple
  'bg-lime-100 text-lime-700 border-lime-200 dark:bg-lime-900/30 dark:text-lime-400 dark:border-lime-800',               //  7. lime
  'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800',     //  8. orange
  'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-900/30 dark:text-fuchsia-400 dark:border-fuchsia-800', //  9. fuchsia
  'bg-pink-100 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-400 dark:border-pink-800',               // 10. pink
  'bg-blue-200 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700',               // 11. blue (deeper)
  'bg-teal-200 text-teal-800 border-teal-300 dark:bg-teal-900/40 dark:text-teal-300 dark:border-teal-700',               // 12. teal (deeper)
  'bg-cyan-200 text-cyan-800 border-cyan-300 dark:bg-cyan-900/40 dark:text-cyan-300 dark:border-cyan-700',               // 13. cyan (deeper)
  'bg-indigo-200 text-indigo-800 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700',     // 14. indigo (deeper)
  'bg-violet-200 text-violet-800 border-violet-300 dark:bg-violet-900/40 dark:text-violet-300 dark:border-violet-700',     // 15. violet (deeper)
  'bg-purple-200 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-700',     // 16. purple (deeper)
  'bg-lime-200 text-lime-800 border-lime-300 dark:bg-lime-900/40 dark:text-lime-300 dark:border-lime-700',               // 17. lime (deeper)
  'bg-orange-200 text-orange-800 border-orange-300 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-700',     // 18. orange (deeper)
  'bg-fuchsia-200 text-fuchsia-800 border-fuchsia-300 dark:bg-fuchsia-900/40 dark:text-fuchsia-300 dark:border-fuchsia-700', // 19. fuchsia (deeper)
  'bg-pink-200 text-pink-800 border-pink-300 dark:bg-pink-900/40 dark:text-pink-300 dark:border-pink-700',               // 20. pink (deeper)
] as const;

/**
 * Build a color map from an ordered list of keys (role codes, category names, etc.).
 * Colors are assigned sequentially — first key gets color 0, second gets color 1, etc.
 * Wraps around after 20 colors. Same list always produces the same mapping.
 */
export function buildTagColorMap(keys: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  keys.forEach((key, index) => {
    map[key] = TAG_COLORS[index % TAG_COLORS.length];
  });
  return map;
}

/**
 * @deprecated Use buildTagColorMap() for sequential assignment instead.
 * Kept for backward compatibility — falls back to first color if no map provided.
 */
export function getTagColor(_key: string): string {
  return TAG_COLORS[0];
}

// Gradient definitions for consistent usage (uses Tailwind classes)
export const themeGradients = {
  // Background gradients
  background: 'bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-indigo-950',
  
  // Card gradients
  card: 'bg-gradient-to-br from-background/90 to-background/70',
  
  // Hero section gradients
  hero: 'bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-indigo-950/50 dark:to-purple-950/50',
} as const;
